import { Description, Edit } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { updateBusinessSummary } from '../../../../../../../lib/api';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import {
  showAlert,
  validatePermissions,
} from '../../../../../../../lib/functions/utils';

interface ObservationsProps {
  user: any;
  observations: string;
  businessIdentifier: string;
  setUp: () => void;
}

const Observations: React.FC<ObservationsProps> = (props) => {
  const { user, observations, businessIdentifier, setUp } = props;
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [observationsInEdition, setObservationsInEdition] =
    useState(observations);

  const handleSave = async () => {
    try {
      setLoading(true);
      const data = {
        observations: observationsInEdition,
      };
      await updateBusinessSummary(dispatch, businessIdentifier, data);
      showAlert(dispatch, 'success', 'Información actualizada correctamente');
      await setUp();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al actualizar la información');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CenteredCircularProgress relative />;

  const hasObservations = observations && observations.trim() !== '';
  const canEdit = validatePermissions(user, 'businesses_create_new');

  return (
    <Paper
      sx={{
        p: 3,
        boxShadow: 'none',
        border: '1px solid #e0e0e0',
        position: 'relative',
        marginTop: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1.2rem',
              lineHeight: 1.1,
            }}
          >
            Observaciones
          </Typography>
          <Chip
            label={editing ? 'Modo Edición' : 'Resumen'}
            size="small"
            color={editing ? 'warning' : 'primary'}
            variant="outlined"
            sx={{ height: '22px', fontSize: '0.75rem' }}
          />
        </Box>
        {!editing && canEdit && (
          <IconButton
            size="small"
            onClick={() => setEditing(true)}
            sx={{
              padding: 1,
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.2)',
              },
            }}
          >
            <Edit sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
          </IconButton>
        )}
        {editing && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <CustomButton
              variant="outlined"
              size="small"
              color="secondary"
              onClick={() => setEditing(false)}
            >
              Cancelar
            </CustomButton>
            <CustomButton
              variant="contained"
              size="small"
              color="primary"
              onClick={handleSave}
              disabled={!validatePermissions(user, 'businesses_create_new')}
            >
              Guardar
            </CustomButton>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {!editing ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box
              sx={{
                backgroundColor: hasObservations
                  ? 'rgba(156, 39, 176, 0.1)'
                  : 'rgba(158, 158, 158, 0.1)',
                borderRadius: '50%',
                p: 0.4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '26px',
                height: '26px',
                mt: 0.5,
                flexShrink: 0,
              }}
            >
              <Description
                sx={{
                  fontSize: '0.9rem',
                  color: hasObservations ? 'secondary.main' : 'text.disabled',
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}
              >
                Observaciones
              </Typography>
              {hasObservations ? (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    color: 'text.primary',
                  }}
                >
                  {observations}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                    color: 'text.disabled',
                    fontStyle: 'italic',
                  }}
                >
                  No hay observaciones registradas
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Box
              sx={{
                backgroundColor: 'rgba(156, 39, 176, 0.1)',
                borderRadius: '50%',
                p: 0.4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '26px',
                height: '26px',
                mt: 0.5,
                flexShrink: 0,
              }}
            >
              <Description
                sx={{ fontSize: '0.9rem', color: 'secondary.main' }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.75rem', display: 'block', mb: 0.5 }}
              >
                Observaciones
              </Typography>
              <TextField
                value={observationsInEdition || ''}
                onChange={(e) => setObservationsInEdition(e.target.value)}
                variant="outlined"
                multiline
                fullWidth
                rows={6}
                placeholder="Ingresar observaciones sobre la empresa..."
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: '6px',
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.85rem',
                    lineHeight: 1.4,
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'text.disabled',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default Observations;
